import { apiJson, apiJsonArray, normalizeApiArray } from '@/lib/api-client';

export interface Message {
  id: number;
  booking_id: number | null;
  sender_id: number;
  receiver_id: number;
  message: string;
  is_read: boolean;
  created_at: string;
}

export interface Conversation {
  id: number;
  property_id: number;
  guest_id: number;
  owner_id: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationWithDetails extends Conversation {
  property_title: string;
  property_images: string;
  property_location: string;
  guest_name: string;
  owner_name: string;
  last_message?: Message;
  unread_count: number;
}

class MessageService {
  async createConversation(
    propertyId: number,
    guestId: number,
    ownerId: number,
  ): Promise<Conversation> {
    return apiJson<Conversation>('api/messages/conversations', {
      method: 'POST',
      body: JSON.stringify({ propertyId, guestId, ownerId }),
    });
  }

  async sendMessage(
    conversationId: number,
    senderId: number,
    content: string,
  ): Promise<Message> {
    return apiJson<Message>(
      `api/messages/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ senderId, content }),
      },
    );
  }

  async getMessages(conversationId: number): Promise<Message[]> {
    return apiJsonArray<Message>(
      `api/messages/conversations/${conversationId}/messages`,
    );
  }

  async getConversations(userId: number): Promise<ConversationWithDetails[]> {
    const data = await apiJson<unknown>(
      `api/messages/users/${userId}/conversations`,
    );
    return normalizeApiArray<ConversationWithDetails>(data);
  }

  async getConversationById(
    conversationId: number,
  ): Promise<ConversationWithDetails> {
    return apiJson<ConversationWithDetails>(
      `api/messages/conversations/${conversationId}`,
    );
  }

  async markMessagesAsRead(
    conversationId: number,
    userId: number,
  ): Promise<void> {
    await apiJson(`api/messages/conversations/${conversationId}/read`, {
      method: 'PATCH',
      body: JSON.stringify({ userId }),
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('messageUpdated'));
    }
  }
}

export const messageService = new MessageService();
